import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LEVELS, ROWS, POS, locId, fmt, breakdown } from "./shelfShared";

export function AssignModal({ racks, variants, assignedByKey, category, initialRackId, initialLocation, preselectKey, existingSlot, onClose, onSaved }) {
  const [rackId, setRackId] = useState(initialRackId || racks[0]?.id || "");
  const [location, setLocation] = useState(initialLocation || "");
  const [key, setKey] = useState(existingSlot ? existingSlot.type_id + (existingSlot.design_id ? `::${existingSlot.design_id}` : "") : (preselectKey || ""));
  const [q, setQ] = useState("");
  const [qty, setQty] = useState(existingSlot ? String(existingSlot.qty) : "1");
  const [unit, setUnit] = useState(existingSlot ? existingSlot.unit : "pallets");
  const [saving, setSaving] = useState(false);

  const rack = racks.find((r) => r.id === rackId);
  const emptyLocations = useMemo(() => {
    if (!rack) return [];
    const all = [];
    LEVELS.forEach((l) => ROWS.forEach((r) => POS.forEach((p) => all.push(locId(rack.code, l, r, p)))));
    return all.filter((loc) => !(rack.slots || {})[loc] || (existingSlot && loc === initialLocation));
  }, [rack, existingSlot, initialLocation]);

  const v = variants.find((x) => x.key === key);
  const qn = parseFloat(qty) || 0;
  const unitsToAssign = v ? (unit === "pallets" ? qn * (v.upp || 0) : unit === "cases" ? qn * (v.upc || 0) : qn) : 0;
  const assignedOther = v ? (assignedByKey[v.key] || 0) - (existingSlot ? Number(existingSlot.units || 0) : 0) : 0;
  const unassignedAvail = v ? v.stock - assignedOther : 0;
  const overStock = v && unitsToAssign > unassignedAvail + 1e-6;
  const overPallet = v && unit === "cases" && v.cpp && qn > v.cpp;
  const missingPack = v && ((unit === "pallets" && !v.upp) || (unit === "cases" && !v.upc));

  const filtered = variants
    .filter((x) => category === "all" || x.category === category)
    .filter((x) => !q.trim() || x.label.toLowerCase().includes(q.trim().toLowerCase()));

  const submit = async () => {
    if (!v || !rackId || !location || qn <= 0 || overStock || missingPack) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/racks/${rackId}/slots/${location}`, {
        type_id: v.type_id, design_id: v.design_id, qty: qn, unit,
      });
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-md" data-testid="assign-modal">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{existingSlot ? "Edit shelf quantity" : "Assign inventory to slot"}</DialogTitle>
          <DialogDescription>Uses existing component packaging for conversions. Assignments never change Units in Stock.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Location</label>
            <div className="grid grid-cols-2 gap-2">
              <Select value={rackId} onValueChange={(val) => { setRackId(val); setLocation(""); }}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="assign-rack-select"><SelectValue placeholder="Rack" /></SelectTrigger>
                <SelectContent>{racks.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="assign-location-select"><SelectValue placeholder="Position" /></SelectTrigger>
                <SelectContent className="max-h-64">{emptyLocations.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Component</label>
            {!existingSlot && (
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search components…" className="rounded-sm" data-testid="assign-component-search" />
            )}
            {existingSlot ? (
              <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">{v?.label}</div>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded-sm border border-slate-200" data-testid="assign-component-list">
                {filtered.length === 0 ? <p className="p-3 text-xs text-slate-400">No matching components.</p> :
                  filtered.map((x) => (
                    <button key={x.key} onClick={() => setKey(x.key)}
                      className={`block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${key === x.key ? "bg-[#002FA7]/5 font-semibold text-[#002FA7]" : "text-slate-700"}`}
                      data-testid={`assign-option-${x.key}`}>
                      {x.name} <span className="text-xs text-slate-400">· {x.sub}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Quantity</label>
              <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-sm text-right tabular-nums" data-testid="assign-qty-input" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unit</label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="assign-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pallets">Pallets</SelectItem>
                  <SelectItem value="cases">Cases</SelectItem>
                  <SelectItem value="units">Units</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {v && (
            <div className="rounded-sm bg-slate-50 p-3 text-xs text-slate-600" data-testid="assign-conversion">
              <div>Packaging: {v.upc ? `${fmt(v.upc)}/case` : "no case size"}{v.upp ? ` · ${fmt(v.upp)}/pallet` : ""}</div>
              <div className="mt-1 font-semibold text-slate-800">This slot = {breakdown(unitsToAssign, v.upc, v.upp).join(" · ")}</div>
              <div className="mt-1">Unassigned available: {fmt(unassignedAvail)} units</div>
            </div>
          )}

          {missingPack && <div className="flex items-center gap-2 text-xs text-red-600" data-testid="assign-missing-pack"><AlertTriangle className="h-4 w-4" /> This component has no {unit === "pallets" ? "units-per-pallet" : "units-per-case"} packaging. Set it on the variant first.</div>}
          {overStock && !missingPack && <div className="flex items-center gap-2 text-xs text-red-600" data-testid="assign-over-stock"><AlertTriangle className="h-4 w-4" /> Exceeds unassigned stock ({fmt(unassignedAvail)} units available).</div>}
          {overPallet && <div className="flex items-center gap-2 text-xs text-amber-600" data-testid="assign-over-pallet"><AlertTriangle className="h-4 w-4" /> {qn} cases exceeds this component's {v.cpp} cases per pallet (one pallet position).</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving || !v || !location || qn <= 0 || overStock || missingPack} onClick={submit} data-testid="assign-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (existingSlot ? "Update" : "Assign")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
